import React, { useMemo, useRef, useEffect, useState, FocusEvent } from "react";
import { ReactDatePickerProps } from "react-datepicker";
import { TimeField, ITimeFieldProps } from "./TimeField";
import moment from "moment-timezone";
import { isIsoDate, isEpochDate } from "src/utils/index";
// Material-UI
import { useTheme } from "@mui/material/styles";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import { Button } from "@mui/material";
import classNames from "classnames";
import Tooltip from "@mui/material/Tooltip";
import { TextFieldStyles } from "./TextField";
// Formik imports
import { FieldMetaProps } from "formik";
import { useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { FieldError } from "./FieldError";
import { useCallback } from "react";
import { yearInMilliseconds } from "@vmosolutions/oc-interfaces";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { CalendarIconField } from "./CalendarIconField";

export interface IDateTimeFieldProps {
  name: string;
  value?: string | number;
  "data-testid"?: string;
  label?: string;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onChange?: (value: string | number) => any;
  className?: string;
  showAirportTime?: string;
  hideTimeControl?: boolean;
  hideLocalizeBtn?: boolean;
  localizeDisplay?: boolean;
  displayOnlyTime?: boolean;
  isEpoch?: boolean;
  disabled?: boolean;
  DateProps?: Partial<ReactDatePickerProps>;
  TimeProps?: ITimeFieldProps;
  meta?: Partial<FieldMetaProps<any>>;
  minDate?: string | number;
  maxDate?: string | number;
  showCalendarIcon?: boolean;
  labelStyle?: object;
}

interface IValidateDateTimeResponse {
  value: string | number;
  valid: boolean;
  errorString: string;
  date?: { value: string; valid: boolean };
  time?: { value: string; valid: boolean };
  remainder?: { value: string; valid: boolean };
}

export const validateDateTimeFormat = (value: string | number): IValidateDateTimeResponse => {
  if (!value) {
    return { value: "", valid: true, errorString: "" };
  }

  if (isEpochDate(value)) {
    return { value: value, valid: true, errorString: "" };
  }

  const checkDate = isIsoDate(value as string);

  let errorStr = "";
  // ISO Date value is incorrect formatted (unknown issue)
  if (!checkDate.valid && checkDate.type === "unknown") {
    errorStr = "Date/time format incorrect.";
  } else if (!checkDate.valid && checkDate.type === "isoString") {
    // Format has been detected as ressembling an ISO string but a component is incorrect
    const dateStr = checkDate.value.slice(0, 10);
    const timeStr = checkDate.value.slice(11, 16);
    const remainderStr = checkDate.value.slice(17, 24);
    // Check Date
    const invalidDate = !/^\d{4}-(0[1-9]|1[012])-(0[1-9]|[12][0-9]|3[01])$/.test(dateStr);
    const invalidTime = !/^(0[0-9]|1[0-9]|2[0-3]|[0-9]):[0-5][0-9]$/.test(timeStr);
    const invalidRemainder = !/^([0-5][0-9])(.[0-9]+)?(Z)?$/.test(remainderStr);

    switch (true) {
      case invalidDate:
        errorStr = "Date format incorrect.";
        break;
      case invalidTime:
        errorStr = "Time format incorrect.";
        break;
      case invalidRemainder:
        errorStr = "Timezone incorrect.";
        break;
      default:
      // do nothing
    }
    return {
      value: value,
      valid: errorStr.length === 0,
      errorString: errorStr,
      date: { value: dateStr, valid: !invalidDate },
      time: { value: timeStr, valid: !invalidTime },
      remainder: { value: remainderStr, valid: !invalidRemainder },
    };
  }
  return { value: value, valid: errorStr.length === 0, errorString: errorStr };
};

// Adds timezone offset when supplied with UTC Date.
const returnAirportDateTime = (utcDateTime: string, timezone: string): string => {
  if (!timezone || !isIsoDate(utcDateTime)) {
    return utcDateTime;
  }
  const UTCTimezoneOffset = moment(utcDateTime).tz(timezone).utcOffset(); // Returns the utc offset of the supplied timezone.
  const momentDate = moment(utcDateTime);
  momentDate.add(UTCTimezoneOffset, "minutes");
  return momentDate.toISOString();
};

// Removes offset when supplied with the airport Date string.
const returnUTCDateTime = (airportDateTime: string, timezone: string): string => {
  if (!timezone || !isIsoDate(airportDateTime)) {
    return airportDateTime;
  }
  const UTCTimezoneOffset = moment(airportDateTime).tz(timezone).utcOffset(); // Returns the utc offset of the supplied timezone.
  const momentDate = moment(airportDateTime);
  momentDate.subtract(UTCTimezoneOffset, "minutes");
  return momentDate.toISOString();
};

// Extract the time from the supplied ISO string (gets passed to the time input component.)
const extractTime = (IsoDate: string): string => IsoDate.substr(11, 5);

/**
 * DATE TIME INPUT component
 */
export const DateTimeField = (props: IDateTimeFieldProps): JSX.Element => {
  const {
    name,
    "data-testid": dataTestId,
    label,
    onFocus,
    onBlur,
    value,
    onChange,
    className,
    hideTimeControl,
    showAirportTime,
    displayOnlyTime,
    hideLocalizeBtn,
    localizeDisplay,
    isEpoch,
    disabled,
    DateProps,
    TimeProps,
    meta,
    minDate = new Date()?.valueOf() - 100 * yearInMilliseconds,
    maxDate = new Date()?.valueOf() + 100 * yearInMilliseconds,
    showCalendarIcon,
    labelStyle,
  } = props;

  const labelRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const DatePickerRef = useRef(null);
  const [displayAsUTC, setdisplayAsUTC] = useState<boolean>(true);
  const [airportIdent, setAirportIdent] = useState<string>(null);
  const [timefieldValue, setTimeFieldValue] = useState<string>(null);
  const [timefieldError, setTimeFieldError] = useState<string>(null);
  const [timezone, setTimezone] = useState<string>(null);
  const [utcDateValue, setUtcDateValue] = useState<string>(null);
  const [airportDateValue, setAirportDateValue] = useState<string>(null);
  const [valueIsEpoch, setValueIsEpoch] = useState<boolean>(isEpoch);
  const [displayDate, setDisplayDate] = useState(displayAsUTC ? utcDateValue : airportDateValue);
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const airportOptions = useSelector((state: IRootState) => state.structures.airportOptions);
  // Set displayed Date to UTC or Airport Date depending on which one the user has selected.

  // If localizeDisplay props changes, modify internal display value.
  useEffect(() => {
    const showLocal = localizeDisplay && !!showAirportTime;
    setdisplayAsUTC(!showLocal);
  }, [localizeDisplay, showAirportTime]);

  useEffect(() => {
    setDisplayDate(displayAsUTC ? utcDateValue : airportDateValue);
  }, [displayAsUTC, utcDateValue, airportDateValue]);

  // Track if formik value is dirty.
  const dirty: boolean = useMemo(() => {
    if (!meta) return false;
    const { initialValue, touched } = meta;
    return touched && value !== initialValue ? true : false;
  }, [value, meta]);

  const isValidDate = useMemo(() => displayOnlyTime || !value || moment(value).isValid(), [displayOnlyTime, value]);

  useEffect(() => {
    const isE =
      isEpoch || (value !== null && value !== undefined && typeof value === "number" && !!new Date(value))
        ? true
        : false;
    // Check to make certain the value is a valid date type.
    const isValid = displayOnlyTime || moment(value).isValid();
    if (isValid) {
      const dateValue =
        isE && value !== null && value !== undefined ? new Date(value).toISOString() : isE && !value ? null : value;
      setValueIsEpoch(isE);
      setUtcDateValue(dateValue as string);
    }
  }, [name, value, isEpoch, displayOnlyTime]);

  // On update airportIdent, retrieve retrieve and set timezone for associated airport.
  useEffect(() => {
    if (!displayOnlyTime && !!showAirportTime && airportIdent !== showAirportTime) {
      const newTimezone = airportOptions[showAirportTime]?.timezoneId;
      if (newTimezone) {
        setTimezone(newTimezone);
        setAirportIdent(showAirportTime);
      }
    }
  }, [airportIdent, airportOptions, showAirportTime, displayOnlyTime]);

  // On update of timezone or utcdate, fetch airportDateTime.
  useEffect(() => {
    if (!!timezone && !!utcDateValue) {
      const airportDateTime = returnAirportDateTime(utcDateValue, timezone);
      setAirportDateValue(airportDateTime);
    } else {
      setAirportDateValue(null);
    }
  }, [timezone, utcDateValue]);

  // Since React DatePicker only works with local time internally, shift the date being passed in so the timezone date selected will always appear correct.
  const displayAsLocal = (isoString: string): Date => {
    if (!isoString) {
      return null;
    }
    const tempDate = new Date(isoString);
    // Add a timezone offset to the UTC time (as the datepicker displays in local mode only) (IN)
    const localDate = new Date(tempDate.getTime() + tempDate.getTimezoneOffset() * 60000);
    return localDate;
  };

  // Convert incoming min/max epochs or isotrings into Date objects compatible with the DatePicker.
  const adjustedDateProps = useMemo(() => {
    if (!DateProps && !maxDate && !minDate) {
      return null;
    }
    const applyLocalShift = timezone && !displayAsUTC;
    const isoMin =
      minDate && typeof minDate === "string" ? minDate : minDate ? new Date(minDate).toISOString() : undefined;
    const isoMax =
      maxDate && typeof maxDate === "string" ? maxDate : maxDate ? new Date(maxDate).toISOString() : undefined;

    //!! Made certain any timezone offsets applied to the value, are also applied to the min and max values.
    const adjustedMinDate =
      isoMin && applyLocalShift
        ? displayAsLocal(returnAirportDateTime(isoMin, timezone))
        : isoMin && !applyLocalShift
          ? displayAsLocal(isoMin)
          : undefined;
    const adjustedMaxDate =
      isoMax && applyLocalShift
        ? displayAsLocal(returnAirportDateTime(isoMax, timezone))
        : isoMax && !applyLocalShift
          ? displayAsLocal(isoMax)
          : undefined;
    return { ...DateProps, minDate: adjustedMinDate, maxDate: adjustedMaxDate };
  }, [DateProps, maxDate, minDate, timezone, displayAsUTC]);

  // Checks the value that was input into the component and returns the same type.
  const outputSameAsInputType = useCallback(
    (date: string): number | string => {
      return valueIsEpoch ? new Date(date)?.valueOf() : date;
    },
    [valueIsEpoch]
  );

  // HANDLE DATE CHANGE
  const handleDateChange = useCallback(
    (event: any, context: any): void => {
      const newDate = event ? new Date(event) : null;
      if (!!newDate && typeof newDate.toISOString === "function") {
        if (isNaN(newDate.getTime())) {
          setDisplayDate(event.toString());
          return;
        }
        // Remove the timezone offset applied to the DatePicker local time. (OUT)
        const eventDate = new Date(newDate.getTime() - newDate.getTimezoneOffset() * 60000).toISOString();
        // If UTC is on, apply offset to airport time. If off, remove it to get UTC Date.
        const newUTCtime = displayAsUTC ? eventDate : returnUTCDateTime(eventDate, timezone);
        const newAirportTime = displayAsUTC ? returnAirportDateTime(eventDate, timezone) : eventDate;
        setUtcDateValue(newUTCtime);
        setAirportDateValue(newAirportTime);
        if (onChange) onChange(outputSameAsInputType(newUTCtime));
      } else {
        setUtcDateValue(null);
        setAirportDateValue(null);
        if (onChange) onChange(null);
      }
    },
    [displayAsUTC, onChange, outputSameAsInputType, timezone]
  );

  // HANDLE TIME CHANGE
  const handleTimeChange = useCallback(
    (time: string, error?: string): void => {
      // If there's a time field error, return without modifying date.
      if (error) {
        setTimeFieldValue(time);
        setTimeFieldError(error);
        return;
      }
      const value = time ?? "00:00";
      let newDisplayedValue: string;
      if (displayOnlyTime !== true) {
        const previousDate = displayAsUTC ? utcDateValue : airportDateValue;
        const oldMinutes = parseInt(previousDate.slice(11, 13)) * 60 + parseInt(previousDate.slice(14, 16));
        const newMinutes = parseInt(value.slice(0, 2)) * 60 + parseInt(value.slice(3, 5));
        const minuteShift = newMinutes - oldMinutes;
        // Apply the time shift and update values.
        const newUTCtime = moment(utcDateValue).add(minuteShift, "minutes").toISOString();
        const newAirportTime = moment(airportDateValue).add(minuteShift, "minutes").toISOString();
        setUtcDateValue(newUTCtime);
        setAirportDateValue(newAirportTime);
        newDisplayedValue = newUTCtime;
      } else {
        newDisplayedValue = value;
      }
      setTimeFieldValue(null);
      setTimeFieldError(null);
      if (onChange) onChange(outputSameAsInputType(newDisplayedValue));
    },
    [airportDateValue, displayAsUTC, displayOnlyTime, onChange, outputSameAsInputType, utcDateValue]
  );

  const handleFocusDate = useCallback(
    (e: FocusEvent<HTMLInputElement>): void => {
      setIsFocused(true);
      DatePickerRef?.current?.setOpen(true);
      if (onFocus && typeof onFocus === "function") {
        onFocus(e);
      }
    },
    [DatePickerRef, onFocus]
  );

  const handleBlurDate = useCallback(
    (e: FocusEvent<HTMLInputElement>): void => {
      setIsFocused(false);
      if (onBlur && typeof onBlur === "function") {
        onBlur(e);
      }
    },
    [onBlur]
  );

  const handleFocusTime = useCallback(
    (e: FocusEvent<HTMLInputElement>): any => {
      if (!showCalendarIcon) {
        DatePickerRef?.current?.setOpen(true);
      }
      setIsFocused(true);
      if (onFocus && typeof onFocus === "function") {
        onFocus(e);
      }
    },
    [DatePickerRef, onFocus, showCalendarIcon]
  );

  /** Blurring calendar has to be delayed for time picker so that date selections can be made before it closes. - CC */
  const handleBlurTime = useCallback(
    (e: FocusEvent<HTMLInputElement>): any => {
      e.persist();
      setTimeout(() => {
        if (onBlur && typeof onBlur === "function") {
          onBlur(e);
        }
      }, 0); // Setting this from 500 to 0 to resolve a tabbing sort order bug. Not certain why I had originally set it to 500 but it seems to work fine with 0 - CC
    },
    [onBlur]
  );
  const hasError = meta?.error || timefieldError || !isValidDate ? true : false;
  const labelSx = labelStyle ?? {};

  const DateTimeFieldStyles = (theme: any): any => ({
    root: {
      display: "flex",
      flexDirection: "column",
      margin: "5px",
      flexWrap: "wrap",
      width: "calc(100% - 10px)",
      "& .MuiFormLabel-root": {
        overflowWrap: "normal !important",
        whiteSpace: "normal !important",
        overflow: "initial !important",
        textOverflow: "initial !important",
        maxWidth: "unset !important",
      },
      "& .hideMuiLabel .MuiFormLabel-root": {
        display: "none !important",
      },
      "&.split_label_input": {
        width: "calc(100% - 10px)",
        "& .input_control": {
          "& .label": {
            width: "50%",
            maxHeight: "24px",
            margin: "5px 10px auto auto",
            "& label": { width: "100%", marginRight: 0 },
          },
          "& .react-datepicker-wrapper": {
            width: "50%",
            maxHeight: "24px",
          },
          "& .time-input-container": {
            width: "50%",
            maxHeight: "26px",
            margin: 0,
            "& input": { width: "100%" },
          },
        },
      },
      "&.auto_width .input_control label": { width: "auto" },
      "& .input_control": {
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        "& .label": {
          display: "flex",
          justifyContent: "center",
          "& label": {
            position: "relative",
            width: "80px",
            textAlign: "right",
            transform: "none",
            color: theme.custom.forms.pristineColor,
            fontSize: 12,
            lineHeight: "12px",
            margin: "auto 10px auto auto",
            transition: theme.transitions.create(["color"]),
          },
        },
        "& .open_calendar_btn": {
          padding: 0,
          marginRight: "-5px",
          "&.hidden svg": { fill: theme.palette.grey[500] },
          "&.visible svg": { fill: theme.palette.primary.main },
          "& svg": { width: "18px" },
        },
        "& .react-datepicker__aria-live": {
          display: "none",
        },
        "& .react-datepicker-wrapper": {
          margin: "0px 4px 0px 0px",
          "& .react-datepicker__input-container": {
            "& input": {
              fontSize: 12,
              fontWeight: 300,
              boxSizing: "border-box",
              width: "100px",
              height: "24px",
              padding: "2px 8px",
              borderRadius: "2px",
              position: "relative",
              color: theme.custom.forms.inputText,
              backgroundColor: theme.custom.forms.inputBack,
              border: "1px solid " + theme.custom.forms.inputBorder,
              transition: theme.transitions.create(["border-color", "color"]),
              "&::placeholder, &::-webkit-input-placeholder": {
                color: theme.custom.forms.labels,
              },
              "&:hover": {
                borderColor: theme.custom.forms.pristineColor,
              },
              "&:focus": {
                outlineOffset: "none",
                outline: "none",
              },
            },
            "& button.react-datepicker__close-icon": {
              margin: "4px",
              top: 0,
              right: 0,
              "&:after": { borderRadius: "3px" },
            },
            "&:after": {
              content: '""',
              transition: theme.transitions.create(["transform"]),
              left: 0,
              right: 0,
              bottom: 0,
              position: "absolute",
              transform: "scaleX(0)",
              borderBottom: "2px solid " + theme.custom.forms.focusedColor,
              pointerEvents: "none",
            },
          },
        },
        "& .time-input-container": {
          position: "relative",
          display: "inline-block",
          margin: "auto 4px auto auto",
          "& .time-input": {
            boxSizing: "border-box",
            fontFamily: "Heebo, sans-serif",
            fontWeight: 300,
            width: "60px",
            height: "24px",
            padding: "2px",
            borderRadius: "2px",
            position: "relative",
            color: theme.custom.forms.inputText,
            backgroundColor: theme.custom.forms.inputBack,
            border: "1px solid " + theme.custom.forms.inputBorder,
            textAlign: "center",
            fontSize: 12,
            transition: theme.transitions.create(["border-color", "color"]),
            "&::placeholder, &::-webkit-input-placeholder": {
              color: theme.custom.forms.labels,
            },
            "&:hover": {
              borderColor: theme.custom.forms.pristineColor,
            },
            "&:focus": {
              outlineOffset: "none",
              outline: "none",
            },
          },
          "&:after": {
            content: '""',
            transition: theme.transitions.create(["transform"]),
            left: 0,
            right: 0,
            bottom: 0,
            position: "absolute",
            transform: "scaleX(0)",
            borderBottom: "2px solid " + theme.custom.forms.focusedColor,
            pointerEvents: "none",
          },
        },
        "& .utc-switch": {
          margin: "24px 0px 0px",
          height: "20px",
        },
        "& .timeformat-buttons": {
          display: "flex",
          "& button": {
            height: "23px",
            minWidth: "41px",
            maxWidth: "41px",
            padding: 0,
            fontSize: 10,
            "&.disabled": {
              color: theme.palette.primary.contrastText,
              backgroundColor: theme.palette.primary.main,
            },
          },
          "& button.timeUTCButton": {
            borderRadius: "2px 0px 0px 2px",
          },
          "& button.timeAirportButton": {
            borderRadius: "0px 2px 2px 0px",
            display: "inline-block",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            "&.MuiButton-contained": {
              backgroundColor: theme.palette.warning.main,
              color: theme.palette.background.default,
            },
          },
        },
        "& .timezone_tooltip": { display: "flex", flexWrap: "nowrap" },
      },
      "&.local .input_control": {
        "& input": {
          color: theme.palette.secondary.light,
          "&::placeholder, &::-webkit-input-placeholder": {
            color: theme.palette.secondary.dark,
          },
        },
        "& .time-input-container": {
          "& .time-input": {
            color: theme.palette.secondary.light,
            "&::placeholder, &::-webkit-input-placeholder": {
              color: theme.palette.secondary.dark,
            },
          },
        },
      },
      "&.disabled .input_control .react-datepicker-wrapper": {
        opacity: 0.5,
        userSelect: "none",
        pointerEvents: "none",
      },
      "&.focused .input_control": {
        "& .label label": {
          color: theme.custom.forms.focusedColor,
        },
        "& .react-datepicker-wrapper .react-datepicker__input-container input": {
          border: "1px solid " + theme.custom.forms.pristineColor,
        },
        "& .time-input-container .time-input": {
          border: "1px solid " + theme.custom.forms.pristineColor,
        },
        "& .react-datepicker-wrapper .react-datepicker__input-container:after": {
          transform: "scaleX(1)",
        },
        "& .time-input-container:after": {
          transform: "scaleX(1)",
        },
      },
      "&.dirty .input_control": {
        "& .label label": {
          color: `${theme.custom.forms.dirtyColor} !important`,
        },
        "& .MuiTextField-root": {
          border: "1px solid " + `${theme.custom.forms.dirtyColor} !important`,
        },
        "& .react-datepicker-wrapper .react-datepicker__input-container input .MuiTextField-root": {
          border: "1px solid " + theme.custom.forms.dirtyColor,
        },
        "& .time-input-container .time-input": {
          border: "1px solid " + `${theme.custom.forms.dirtyColor} !important`,
        },
        "& .open_calendar_btn svg": {
          fill: `${theme.custom.forms.dirtyColor} !important`,
        },
      },
      "&.error .input_control": {
        "& .label label": {
          color: theme.custom.forms.errorColor,
        },
        "& .open_calendar_btn svg": {
          fill: `${theme.custom.forms.errorColor} !important`,
        },
        "& .react-datepicker-wrapper .react-datepicker__input-container": {
          "&:after": {
            transform: "scaleX(1)",
            borderBottom: "2px solid " + theme.custom.forms.errorColor,
          },
        },
        "& .time-input-container": {
          "&:after": {
            transform: "scaleX(1)",
            borderBottom: "2px solid " + theme.custom.forms.errorColor,
          },
        },
      },
      "& .error-message": {
        maxWidth: 300,
        padding: "0px 5px",
        fontWeight: 300,
        color: theme.palette.error.main,
      },
    },
    selectedDay: {
      "&.MuiPickersDay-today": {
        borderRadius: "15%",
      },
      "&.Mui-selected": {
        borderRadius: "15%",
      },
    },
  });

  // NOTE: When rendering tooltips on buttons, you have to add a div wrapper as the disabled state of buttons will break the tooltip functionality.
  // see https://github.com/mui-org/material-ui/issues/8416 for reference.
  return (
    <FormControl
      data-testid={dataTestId}
      className={classNames(
        className,
        disabled && "disabled",
        isFocused && "focused",
        dirty && "dirty",
        hasError && "error",
        timezone && !displayAsUTC && "local"
      )}
      sx={(theme) => DateTimeFieldStyles(theme).root}
      error={hasError}
    >
      <div className="input_control">
        {label && (
          <div ref={labelRef} className="label">
            <InputLabel htmlFor={"id-" + name + "-date"} sx={labelSx}>
              {label}
            </InputLabel>
          </div>
        )}

        <Tooltip
          enterDelay={500}
          placement="right"
          title={displayOnlyTime !== true ? (timezone && !displayAsUTC ? timezone : "UTC") : ""}
          disableHoverListener={(!timezone && !displayAsUTC) || !hideLocalizeBtn || disabled}
          disableFocusListener={(!timezone && !displayAsUTC) || !hideLocalizeBtn || disabled}
          disableTouchListener={(!timezone && !displayAsUTC) || !hideLocalizeBtn || disabled}
        >
          <div className="timezone_tooltip">
            {displayOnlyTime !== true && (
              <>
                {!showCalendarIcon && (
                  <DatePicker
                    disabled={disabled}
                    value={displayDate ? moment(displayAsLocal(displayDate.toString())) : null}
                    onChange={(value, context) => handleDateChange(value, context)}
                    format="MMM DD, YYYY"
                    minDate={moment(adjustedDateProps.minDate)}
                    maxDate={moment(adjustedDateProps.maxDate)}
                    slotProps={{
                      actionBar: {
                        actions: ["clear"],
                      },
                      openPickerButton: {
                        tabIndex: -1,
                        sx: {
                          padding: "2px !important",
                        },
                      },
                      textField: {
                        id: `${name}_d_input`,
                        className: "input-base",
                        inputProps: dataTestId ? { "data-testid": `${dataTestId}-input` } : undefined,
                        sx: TextFieldStyles(theme, null).dateTime,
                      },
                      day: {
                        classes: { root: DateTimeFieldStyles(theme).selectedDay },
                      },
                    }}
                  />
                )}
                {showCalendarIcon && (
                  <CalendarIconField
                    value={moment(displayAsLocal(displayDate?.toString()))}
                    disabled={disabled}
                    offset={{ x: 0, y: -400 }}
                    onChange={(value, context) => handleDateChange(value.toDate(), context)}
                    minDate={moment(adjustedDateProps.minDate)}
                    maxDate={moment(adjustedDateProps.maxDate)}
                  />
                )}
              </>
            )}

            {(hideTimeControl !== true || !!showAirportTime) && (
              <TimeField
                id={name + "_t_input"}
                disabled={(!displayDate && displayOnlyTime !== true) || disabled}
                placeholder="HH:mm"
                value={
                  !!displayDate && displayOnlyTime !== true
                    ? timefieldValue || extractTime(displayDate)
                    : timefieldValue || displayDate
                }
                onFocus={handleFocusTime}
                onBlur={handleBlurTime}
                onChange={handleTimeChange}
                {...TimeProps}
              />
            )}
          </div>
        </Tooltip>

        {!!showAirportTime && displayOnlyTime !== true && hideLocalizeBtn !== true && (
          <div className="timeformat-buttons">
            <Tooltip title="Coordinated Universal Time" enterDelay={1000} placement="top">
              <span>
                <Button
                  id="time_utc_btn"
                  variant={displayAsUTC ? "contained" : "outlined"}
                  className="timeUTCButton"
                  color="primary"
                  classes={{ disabled: "disabled" }}
                  disabled={displayAsUTC}
                  onClick={() => setdisplayAsUTC(true)}
                >
                  UTC
                </Button>
              </span>
            </Tooltip>
            <Tooltip title={timezone ? timezone : ""} enterDelay={1000} placement="top">
              <span>
                <Button
                  id="time_air_btn"
                  variant={displayAsUTC ? "outlined" : "contained"}
                  className="timeAirportButton"
                  color="primary"
                  classes={{ disabled: "disabled" }}
                  disabled={!displayAsUTC}
                  onClick={() => setdisplayAsUTC(false)}
                >
                  {showAirportTime}
                </Button>
              </span>
            </Tooltip>
          </div>
        )}
      </div>
      {/* Display field error is meta error is passed or if hasError is true (* used for date validation and timefield error display) */}
      {((typeof meta?.error === "string" && meta?.error !== "") || (!meta?.error && !!hasError)) && (
        <FieldError
          meta={{
            ...meta,
            error: meta?.error ? meta.error : (timefieldError ?? (isValidDate ? undefined : `Invalid Value: ${value}`)),
          }}
        />
      )}
    </FormControl>
  );
};
